import { CSSProperties } from "react";

/**
 * Converts percentage values to em units for horizontal scaling
 * @param percentage The percentage value (0-100)
 */
export const percentageToEm = (percentage: number): string => {
    // Convert to ems (1% = 0.16em for a 16em width container)
    return `${(percentage * 16) / 100}em`;
};

/**
 * Converts percentage values to em units with aspect ratio correction for vertical scaling
 * @param percentage The percentage value (0-100)
 */
export const percentageToEmVertical = (percentage: number): string => {
    // For a 16:9 aspect ratio template (where the width is 16em)
    // The vertical space should be 9em (9/16 = 0.5625 aspect ratio)
    const verticalEmSpace = 9;

    // Convert percentage to em units with aspect ratio correction
    const result = `${(percentage * verticalEmSpace) / 100}em`;
    return result;
};

/**
 * Converts em values to percentage for horizontal positions
 * @param em The em value as a string (with 'em' suffix) or number
 */
export const emToPercentage = (em: string | number): number => {
    const emValue = typeof em === "string" ? parseFloat(em) : em;
    return (emValue * 100) / 16;
};

/**
 * Converts em values to percentage for vertical positions with aspect ratio correction
 * @param em The em value as a string (with 'em' suffix) or number
 */
export const emToPercentageVertical = (em: string | number): number => {
    const emValue = typeof em === "string" ? parseFloat(em) : em;

    // For a 16:9 aspect ratio template (where the width is 16em)
    // The vertical space should be 9em (9/16 = 0.5625 aspect ratio)
    const verticalEmSpace = 9;

    // Convert em units to percentage with aspect ratio correction
    return (emValue * 100) / verticalEmSpace;
};

/**
 * Translates mouse position to percentage position in the template
 * @param mouseX Mouse X coordinate relative to container
 * @param mouseY Mouse Y coordinate relative to container
 * @param containerWidth Container width
 * @param containerHeight Container height
 */
export const mouseToPercentage = (
    mouseX: number,
    mouseY: number,
    containerWidth: number,
    containerHeight: number,
): { x: number; y: number } => {
    // Calculate horizontal percentage (standard calculation)
    const percentX = (mouseX / containerWidth) * 100;

    // Calculate vertical percentage with the same approach
    // No need to apply aspect ratio correction here as it's handled
    // in the conversion functions (percentageToEmVertical)
    const percentY = (mouseY / containerHeight) * 100;

    // Ensure values are within bounds
    const boundedX = Math.max(0, Math.min(100, percentX));
    const boundedY = Math.max(0, Math.min(100, percentY));

    return {
        x: boundedX,
        y: boundedY,
    };
};

/**
 * Get font size from style object
 * @param style CSS style object
 */
export const getFontSize = (style: CSSProperties): number => {
    if (!style.fontSize) return 4;
    // If it's already a number, return it
    if (typeof style.fontSize === "number") return style.fontSize;
    // Extract the base size
    const match = style.fontSize.toString().match(/(\d+(\.\d+)?)/);
    return match ? parseFloat(match[1]!) : 4;
};

/**
 * Extract opacity value from an rgba color string
 * @param rgba RGBA color string
 */
export const getOpacityFromRgba = (rgba: string | undefined): number => {
    if (!rgba) return 0.5;
    const match = rgba.match(/[0-9.]+(?=\))/);
    return match ? parseFloat(match[0]) : 0.5;
};

/**
 * Convert hex color to rgba format
 * @param hex Hex color string
 * @param opacity Opacity value (0-1)
 */
export const hexToRgba = (hex: string, opacity: number): string => {
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    return `rgba(${r}, ${g}, ${b}, ${opacity})`;
};

/**
 * Scale container style for display in the template
 * @param style Original container style
 */
export const scaleContainerStyle = (style: CSSProperties = {}): CSSProperties => {
    const scaledStyle = { ...style };

    // Position scaling
    if (scaledStyle.left) {
        scaledStyle.left = percentageToEm(parseFloat(scaledStyle.left.toString()));
    }

    if (scaledStyle.top) {
        scaledStyle.top = percentageToEmVertical(parseFloat(scaledStyle.top.toString()));
    }

    // Size scaling
    if (scaledStyle.width) {
        scaledStyle.width = percentageToEm(parseFloat(scaledStyle.width.toString()));
    }

    if (scaledStyle.height) {
        scaledStyle.height = percentageToEm(parseFloat(scaledStyle.height.toString()));
    }

    // Border radius scaling
    if (scaledStyle.borderRadius) {
        scaledStyle.borderRadius = scaleBorderRadius(scaledStyle.borderRadius);
    }

    // Make containers not selectable for better dragging experience
    scaledStyle.userSelect = "none";

    return scaledStyle;
};

/**
 * Scale image style for display in the template
 * @param style Original image style
 */
export const scaleImageStyle = (style: CSSProperties = {}): CSSProperties => {
    const scaledStyle = { ...style };

    // Position scaling
    if (scaledStyle.left) {
        scaledStyle.left = percentageToEm(parseFloat(scaledStyle.left.toString()));
    }

    if (scaledStyle.top) {
        scaledStyle.top = percentageToEmVertical(parseFloat(scaledStyle.top.toString()));
    }

    // Size scaling
    if (scaledStyle.width) {
        scaledStyle.width = percentageToEm(parseFloat(scaledStyle.width.toString()));
    }

    if (scaledStyle.height) {
        scaledStyle.height = percentageToEm(parseFloat(scaledStyle.height.toString()));
    }

    // Ensure important image properties are preserved
    scaledStyle.objectFit = scaledStyle.objectFit || "cover";
    scaledStyle.userSelect = "none"; // Make image not selectable for better dragging experience

    return scaledStyle;
};

/**
 * Scale text style for display in the template
 * @param style Original text style
 */
export const scaleTextStyle = (style: CSSProperties = {}): CSSProperties => {
    // Create a new style object with only text-related properties
    const scaledStyle: CSSProperties = {
        // Font properties
        fontSize: style.fontSize ? `${getFontSize(style)}em` : undefined,
        fontWeight: style.fontWeight,
        fontStyle: style.fontStyle,
        textDecoration: style.textDecoration,
        color: style.color,
        textAlign: style.textAlign,
        // Text behavior
        whiteSpace: "nowrap",
        userSelect: "none",
    };

    return scaledStyle;
};

/**
 * Updates image scale and opacity properties
 * @param element Image element to update
 * @param options Object containing scale and/or opacity values
 */
export const updateImageProperties = (
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    element: any,
    options: { scale?: number; opacity?: number },
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
): any => {
    if (!element || element.type !== "image") return element;

    // Extract current scale if it exists in transform
    let currentScale = 1;
    if (element.style?.transform) {
        const scaleMatch = element.style.transform.match(/scale\(([^)]+)\)/);
        if (scaleMatch && scaleMatch[1]) {
            currentScale = parseFloat(scaleMatch[1]);
        }
    }

    // Use provided values or fallback to current values
    const scale = options.scale !== undefined ? options.scale : currentScale;
    const opacity = options.opacity !== undefined ? options.opacity : (element.style?.opacity ?? 1);

    return {
        ...element,
        style: {
            ...element.style,
            transform: `translate(-50%, -50%) scale(${scale})`,
            opacity: opacity,
        },
    };
};

/**
 * Scales border radius appropriately
 * @param radius The border radius value (number or string)
 */
export const scaleBorderRadius = (radius: number | string | undefined): string | undefined => {
    if (!radius) return undefined;
    const value = typeof radius === "number" ? radius : parseFloat(radius);
    return `${value * 0.5}em`;
};
